<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { VForm } from 'vuetify/components/VForm'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import AuthBrandHeader from '@/components/auth/AuthBrandHeader.vue'
import { Modules } from '@/types/generated'
import RegistrationData = Modules.User.RegistrationData
import PrivacyPolicy from '@/components/dialogs/PrivacyPolicy.vue'
import { useUserStore } from '@core/stores/user'

const { t } = useI18n()

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const title = import.meta.env.VITE_APP_RU === 'only' ? 'base_title_ru' : 'base_title'

useHead({
  title: `${t(title)} - ${t('auth.registration_title')}`,
})

const route = useRoute()
const router = useRouter()
const { locale } = useI18n({ useScope: 'global' })

const isPasswordVisible = ref<boolean>(false)
const formInProcess = ref<boolean>(false)
const ability = useAbility()

const errors = ref<Record<string, string | undefined>>({
  name: undefined,
  last_name: undefined,
  patronymic: undefined,
  email: undefined,
  phone: undefined,
  password: undefined,
  timezone: undefined,
  password_confirmation: undefined,
  privacy_policies: undefined,
})

// Отказ, не привязанный к полю (лимит мест организации): текст приходит с бэкенда уже переведённым.
const formErrorMessage = ref<string | null>(null)

const refVForm = ref<VForm>()

const form = ref<RegistrationData>({
  name: '',
  last_name: '',
  patronymic: '',
  email: '',
  phone: '',
  password: '',
  password_confirmation: '',
  privacy_policies: true,
  email_is_verified: false,
  timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone ?? 'Europe/Paris',
})

if (useCookie('registration-is-open').value !== 'yes') {
  await nextTick(() => {
    router.replace('/')
  })
}

const { data: list } = await useApi<any>('/auth/get-timezone-list')

let timezones = [{ title: '1', value: 1 }]
if (list?.value)
  timezones = list.value

const register = async () => {
  formErrorMessage.value = null

  try {
    const res = await $api('/auth/register', {
      method: 'POST',
      body: {
        name: form.value.name,
        last_name: form.value.last_name,
        patronymic: form.value.patronymic,
        email: form.value.email,
        phone: form.value.phone,
        password: form.value.password,
        password_confirmation: form.value.password_confirmation,
        privacy_policies: form.value.privacy_policies,
        timezone: form.value.timezone,
      },
      onResponseError({ response }) {
        formInProcess.value = false
        errors.value = response._data?.errors ?? {}

        if (response._data?.code === 'seat_limit_reached')
          formErrorMessage.value = response._data.message
      },
    })

    useCookie('userAbilityRules').value = res.data.userAbilityRules
    ability.update(res.data.userAbilityRules)

    useCookie('userData').value = res.data.userData

    const userStore = useUserStore()

    userStore.fullName = res.data.userData.fullName

    await nextTick(() => {
      router.replace(route.query.to ? String(route.query.to) : '/')
    })
  }
  catch (err) {
    formInProcess.value = false
    console.error(err)
  }
  formInProcess.value = false
}

const onSubmit = () => {
  refVForm.value?.validate()
    .then(({ valid: isValid }) => {
      if (isValid) {
        formInProcess.value = true
        register()
      }
    })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <div class="position-relative my-sm-16">
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <VCard
        class="auth-card pa-4"
        max-width="448"
      >
        <VCardItem class="justify-center">
          <AuthBrandHeader />
        </VCardItem>

        <VCardText>
          <VForm
            ref="refVForm"
            @submit.prevent="onSubmit"
          >
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.name"
                  :label="t('auth.name')"
                  :error-messages="errors?.name"
                  :rules="[requiredValidator]"
                />
              </VCol>
              <VCol cols="12">
                <AppTextField
                  v-model="form.last_name"
                  :label="t('auth.last_name')"
                  :error-messages="errors?.last_name"
                  :rules="[requiredValidator]"
                />
              </VCol>
              <VCol
                v-if="locale === 'ru'"
                cols="12"
              >
                <AppTextField
                  v-model="form.patronymic"
                  :label="t('auth.patronymic')"
                  :error-messages="errors?.patronymic"
                  :rules="[requiredValidator]"
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  :error-messages="errors?.email"
                  label="Email"
                  type="email"
                  :rules="[emailValidator]"
                  :placeholder="locale === 'ru' ? 'info@mail.ru' : 'johndoe@email.com'"
                />
              </VCol>
<!--              <VCol cols="12">-->
<!--                <AppAutocomplete-->
<!--                  v-model="form.timezone"-->
<!--                  :label="t('auth.timezone')"-->
<!--                  :error-messages="errors?.timezone"-->
<!--                  :items="timezones"-->
<!--                  item-title="title"-->
<!--                  :rules="[requiredValidator]"-->
<!--                  item-value="value"-->
<!--                  :menu-props="{ maxHeight: 200 }"-->
<!--                />-->
<!--              </VCol>-->
<!--              <VCol-->
<!--                cols="12"-->
<!--                class="pt-6"-->
<!--              >-->
<!--                <VPhoneInput-->
<!--                  v-model="form.phone"-->
<!--                  :country-label="t('auth.country')"-->
<!--                  :label="t('auth.phone')"-->
<!--                  :invalid-message="t('auth.invalid_phone')"-->
<!--                  :error-messages="errors?.phone"-->
<!--                />-->
<!--              </VCol>-->

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :placeholder="t('auth.enter_password')"
                  :label="t('auth.password')"
                  autocomplete="off"
                  :rules="[requiredValidator, passwordValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>
              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password_confirmation"
                  :placeholder="t('auth.confirm_password')"
                  autocomplete="off"
                  :rules="[requiredValidator, confirmedValidator(form.password_confirmation, form.password)]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <div class="d-flex align-center mt-2 mb-4">
                  <VCheckbox
                    id="privacy-policy"
                    v-model="form.privacy_policies"
                    :rules="[requiredValidator]"
                    :error-messages="errors?.privacy_policies"
                    inline
                  />
                  <VLabel
                    for="privacy-policy"
                    style="opacity: 1;"
                  >
                    <span class="me-1">{{ t('auth.agree_too') }}</span>
                    <PrivacyPolicy />
                  </VLabel>
                </div>

                <VAlert
                  v-if="formErrorMessage"
                  type="error"
                  density="compact"
                  variant="tonal"
                  class="mb-4"
                >
                  {{ formErrorMessage }}
                </VAlert>

                <VBtn
                  block
                  :disabled="formInProcess"
                  :loading="formInProcess"
                  type="submit"
                >
                  {{ t('auth.register') }}
                </VBtn>
              </VCol>

              <!-- login instead -->
              <VCol
                cols="12"
                class="text-center text-base"
              >
                <span>{{ t('auth.not_first_time') }}</span>
                <RouterLink
                  class="text-primary ms-2"
                  :to="{ name: 'login' }"
                >
                  {{ t('auth.login') }}
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </div>
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth.scss";
</style>
